/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
/**
 * Typed error that signals a detail-page load returned no entity. Carries the
 * entity code so {@link ErrorBanner} can render a translated, entity-specific
 * "{Entity} not found." message via the domain-entity catalogue (issue 729).
 *
 * Codes map 1:1 to rows in the `domain_entity_types` type table; the catalogue
 * is the runtime-editable single source of truth for user-visible entity names.
 * Adding a new entity = add a row to the catalogue + add the code below (the
 * project's RequireTypeTablesRegisteredInBackend / EnumTypescriptMatch gates
 * keep frontend and backend in lockstep).
 */
export const DomainEntityCode = {
    IDENTITY: 'IDENTITY',
    PARTY: 'PARTY',
    ROLE: 'ROLE',
    ACCESS_PROFILE: 'ACCESS_PROFILE',
    FUNCTION_GROUP: 'FUNCTION_GROUP',
    INBOUND_CONNECTOR: 'INBOUND_CONNECTOR',
    OUTBOUND_CONNECTOR: 'OUTBOUND_CONNECTOR',
    JOB: 'JOB',
    CAMPAIGN: 'CAMPAIGN',
    ACCESS_REQUEST: 'ACCESS_REQUEST',
    RESTRICTION_RULE: 'RESTRICTION_RULE',
    AUTO_ASSIGN_RULE: 'AUTO_ASSIGN_RULE',
    REQUEST: 'REQUEST',
    APPROVAL: 'APPROVAL',
    RESTRICTION_RULE_REQUEST: 'RESTRICTION_RULE_REQUEST',
    AUTO_ASSIGN_RULE_REQUEST: 'AUTO_ASSIGN_RULE_REQUEST',
    FUNCTION_GROUP_REQUEST: 'FUNCTION_GROUP_REQUEST',
    EXECUTION: 'EXECUTION',
    ERROR_LOG: 'ERROR_LOG',
    PROVISIONING_TASK: 'PROVISIONING_TASK',
    CERTIFICATION_POLICY: 'CERTIFICATION_POLICY',
    APPROVAL_POLICY: 'APPROVAL_POLICY',
    REPORT_DEFINITION: 'REPORT_DEFINITION',
    SERVICE_ACCOUNT: 'SERVICE_ACCOUNT',
    NOTIFICATION: 'NOTIFICATION',
    VALUE_LIST: 'VALUE_LIST',
    TYPE: 'TYPE',
    TYPE_VALUE: 'TYPE_VALUE',
    IDENTITY_GRANTS: 'IDENTITY_GRANTS',
    SYSTEM_GRANTS: 'SYSTEM_GRANTS',
    FORM_LAYOUT: 'FORM_LAYOUT',
    REQUEST_INSPECTOR: 'REQUEST_INSPECTOR',
    RESOURCE: 'RESOURCE',
    TARGET_SYSTEM: 'TARGET_SYSTEM',
    ENFORCEMENT_OBJECT: 'ENFORCEMENT_OBJECT',
    ATTRIBUTE_DEFINITION: 'ATTRIBUTE_DEFINITION',
    AUDIT_LOG: 'AUDIT_LOG',
    LIFECYCLE_EVENT: 'LIFECYCLE_EVENT',
    ROLE_ASSIGNMENT: 'ROLE_ASSIGNMENT',
    ACCESS_PROFILE_ASSIGNMENT: 'ACCESS_PROFILE_ASSIGNMENT',
    FUNCTION_GROUP_ASSIGNMENT: 'FUNCTION_GROUP_ASSIGNMENT',
    ACCESS_REQUEST_APPROVAL: 'ACCESS_REQUEST_APPROVAL',
    FUNCTION_GROUP_REQUEST_APPROVAL: 'FUNCTION_GROUP_REQUEST_APPROVAL',
    RESTRICTION_RULE_REQUEST_APPROVAL: 'RESTRICTION_RULE_REQUEST_APPROVAL',
    AUTO_ASSIGN_RULE_REQUEST_APPROVAL: 'AUTO_ASSIGN_RULE_REQUEST_APPROVAL',
    APPROVAL_STEP: 'APPROVAL_STEP',
} as const

export type DomainEntityCode = typeof DomainEntityCode[keyof typeof DomainEntityCode]

export class NotFoundError extends Error {

    public readonly entityCode: DomainEntityCode

    public constructor(entityCode: DomainEntityCode) {
        // i18next/no-literal-string is irrelevant here — the literal `super()`
        // message never reaches the UI; ErrorBanner renders `common.notFound`
        // keyed off entityCode. The technical fallback message is for logs.
        // eslint-disable-next-line i18next/no-literal-string
        super(`${entityCode} not found`)
        // eslint-disable-next-line i18next/no-literal-string
        this.name = 'NotFoundError'
        this.entityCode = entityCode
        Object.setPrototypeOf(this, NotFoundError.prototype)
    }
}

/**
 * Returns a {@link NotFoundError} when {@code loaded} is undefined / null and
 * the loading phase has finished, otherwise returns undefined. The caller is
 * expected to OR this against any in-flight query error so that a real
 * transport / 5xx error is surfaced first:
 *
 * <pre>
 * const error = queryError ?? (!isLoading ? notFoundIfMissing(role, 'ROLE') : undefined)
 * </pre>
 */
export function notFoundIfMissing<T>(
    loaded: T | undefined | null,
    code: DomainEntityCode,
): NotFoundError | undefined {
    return loaded == null ? new NotFoundError(code) : undefined
}
